import React, { useState, useEffect, useRef } from "react";
import { Button, Form, FormControl } from "react-bootstrap";

interface IHSelectOptions {
  buttonClass?: string;
  search?: boolean;
  valueProperty?: string;
  textProperty?: string;
  levelProperty?: string;
  initialValueSet?: boolean;
  showSelectCaption?: boolean;
  value?: any; // Should match the type of selected value
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void; 
}

interface IHSelectProps {
  data: Array<any>; // The hierarchical data array
  name: string; // Added: name prop
  options?: IHSelectOptions;
}

const HSelect: React.FC<IHSelectProps> = ({ data, name, options }) => {
  const defaultOptions = {
    buttonClass: "btn-outline-primary",
    search: true,
    valueProperty: "Id",
    textProperty: "Name",
    levelProperty: "Level",
    initialValueSet: true,
    showSelectCaption: true,
    value: null,
    onChange: undefined,
    ...options, // Merge options with defaults
  };

  const [selectedValue, setSelectedValue] = useState<any>(defaultOptions.value);
  const [filteredData, setFilteredData] = useState<Array<any>>(data); // For search filtering
  const [searchText, setSearchText] = useState<string>("");
  const [isOpen, setIsOpen] = useState<boolean>(false); // Track dropdown open state

  const dropdownButtonRef = useRef<HTMLButtonElement | null>(null); // Correct ref type for button
  const isMounted = useRef<boolean>(false);
  const buttonClassName = defaultOptions.buttonClass ?? "btn-outline-primary";
  const buttonVariant = buttonClassName
    .split(" ")
    .find((cls) => cls.startsWith("btn-") && cls !== "btn")
    ?.replace("btn-", "") ?? "outline-primary";
  const buttonUtilityClasses = buttonClassName
    .split(" ")
    .filter((cls) => cls !== "btn" && !cls.startsWith("btn-"))
    .join(" ");

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    if (selectedValue !== null && defaultOptions.onChange) {
      // Create a custom change event
      const event = new Event('change', { bubbles: true }) as any as React.ChangeEvent<HTMLSelectElement>;

      Object.defineProperty(event, 'target', {
        writable: true,
        value: { name, value: selectedValue }
      });

      defaultOptions.onChange(event); // Trigger the custom change event
    }
  }, [selectedValue]);

  useEffect(() => {
    setSelectedValue(defaultOptions.value ?? null);
  }, [defaultOptions.value, data]);

  useEffect(() => {
    // Filter data based on search text
    if (searchText) {
      const filtered = data.filter((item) =>
        item[defaultOptions.textProperty]
          .toLowerCase()
          .includes(searchText.toLowerCase())
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(data);
    }
  }, [searchText, data]);

  const handleSelect = (value: any) => {
    setSelectedValue(value);
    setIsOpen(false); // Close the dropdown after selecting
  };

  // Function to calculate indentation based on hierarchy level
  const getIndentationStyle = (level: number) => {
    return {
      paddingLeft: `${level * 15}px`, // Adjust padding based on level (15px per level)
    };
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen); // Toggle dropdown visibility
  };

  return (
    <div
      id="ParentPageSelector"
      className="dropdown hierarchy-select w-100"
      style={{ minWidth: "0px" }}
    >
      {/* Button trigger */}
      <Button
        type="button"
        variant={buttonVariant}
        title="parent page select"
        className={`${buttonUtilityClasses} dropdown-toggle hselect-button w-100`.trim()}
        data-bs-toggle="dropdown"
        aria-expanded="false"
        ref={dropdownButtonRef} // Attach the ref correctly to the button
        onClick={toggleDropdown} // Toggle the dropdown on button click
      >
        {selectedValue
          ? filteredData.find(
              (item) => item[defaultOptions.valueProperty] === selectedValue
            )?.[defaultOptions.textProperty] || "Please select..."
          : "Please select..."}
      </Button>

      <div
        className={`dropdown-menu shadow ${isOpen ? "show" : ""}`} // Show/hide based on isOpen state
        style={{ width: "100%", overflow: "hidden", position: "absolute" }}
      >
        {/* Optional search */}
        {defaultOptions.search && (
          <div className="hs-searchbox">
            <FormControl
              type="text"
              placeholder="search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="form-control"
              autoComplete="off"
            />
          </div>
        )}

        {/* List of options */}
        <div className="hs-menu-inner" style={{ maxHeight: "256px", overflowY: "auto" }}>
          {defaultOptions.showSelectCaption && (
            <a
              className="dropdown-item"
              href="#"
              data-value=""
              data-level="0"
              onClick={() => handleSelect(null)}
            >
              select...
            </a>
          )}

          {filteredData.map((item, idx) => (
            <a
              key={idx}
              className={`dropdown-item ${selectedValue === item[defaultOptions.valueProperty] ? "active" : ""}`}
              href="#"
              data-value={item[defaultOptions.valueProperty]}
              data-level={item[defaultOptions.levelProperty]}
              onClick={() => handleSelect(item[defaultOptions.valueProperty])}
              style={getIndentationStyle(item[defaultOptions.levelProperty])} // Apply indentation
            >
              {item[defaultOptions.textProperty]}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HSelect;
